import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  setDoc,
  deleteDoc,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Card, Badge, Button, Select, Label, HelperText, Input } from '../components/ui';
import { createInviteCode } from '../lib/householdSetup';
import { useBillingStatus, useHouseholdBilling, openBillingPortal, startCheckout } from '../lib/billing';
import { humanize } from '../lib/humanize';
import { Copy, Check, UserPlus, Pencil, X, Mail, Trash2, Link2, Unlink, Plus, CreditCard } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { CompanionCard } from '../components/CompanionCard';
import type { Member, Applicant } from '@scholarship-app/types';

interface ActiveCode {
  code: string;
  role: 'parent' | 'applicant';
  applicantId: string | null;
  expiresAt: string;
}

function genId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function HouseholdPage() {
  const { user, membership } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCode, setActiveCode] = useState<ActiveCode | null>(null);
  const [copied, setCopied] = useState(false);

  // Code generation form
  const [inviteRole, setInviteRole] = useState<'parent' | 'applicant'>('parent');
  const [inviteApplicantId, setInviteApplicantId] = useState<string>('');

  // Inline name-editing state
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [memberNameDraft, setMemberNameDraft] = useState('');
  const [savingMember, setSavingMember] = useState(false);

  const [editingApplicantId, setEditingApplicantId] = useState<string | null>(null);
  const [applicantNameDraft, setApplicantNameDraft] = useState('');
  const [savingApplicant, setSavingApplicant] = useState(false);

  // Member removal confirmation
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [removingMember, setRemovingMember] = useState(false);

  // Applicant removal confirmation
  const [applicantToRemove, setApplicantToRemove] = useState<Applicant | null>(null);
  const [removingApplicant, setRemovingApplicant] = useState(false);

  // Add-applicant form
  const [addingApplicant, setAddingApplicant] = useState(false);
  const [newApplicantName, setNewApplicantName] = useState('');
  const [savingNewApplicant, setSavingNewApplicant] = useState(false);

  // Link-applicant-to-member state: maps applicant_id -> chosen member uid
  const [linkDraft, setLinkDraft] = useState<Record<string, string>>({});
  const [linkBusy, setLinkBusy] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !membership) return;
    const unsubMembers = onSnapshot(
      collection(db, 'households', membership.householdId, 'members'),
      (snap) => {
        setMembers(snap.docs.map((d) => d.data() as Member));
        setLoading(false);
      }
    );
    return () => unsubMembers();
  }, [membership]);

  useEffect(() => {
    if (!db || !membership) return;
    const unsubApplicants = onSnapshot(
      collection(db, 'households', membership.householdId, 'applicants'),
      (snap) => {
        setApplicants(snap.docs.map((d) => d.data() as Applicant));
      }
    );
    return () => unsubApplicants();
  }, [membership]);

  const isParent = membership?.role === 'parent';

  // Subscription (v0.2.43) — only the founder manages billing.
  const billing = useBillingStatus();
  const { billingStatus, createdBy } = useHouseholdBilling(membership?.householdId);
  const isFounder = !!user && user.uid === createdBy;
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingErr, setBillingErr] = useState<string | null>(null);

  async function handleManageSubscription() {
    setBillingBusy(true);
    setBillingErr(null);
    try {
      await openBillingPortal();
    } catch (e) {
      setBillingErr(e instanceof Error ? e.message : 'Could not open the billing portal.');
      setBillingBusy(false);
    }
  }

  async function handleSubscribeNow() {
    setBillingBusy(true);
    setBillingErr(null);
    try {
      await startCheckout();
    } catch (e) {
      setBillingErr(e instanceof Error ? e.message : 'Could not start checkout.');
      setBillingBusy(false);
    }
  }

  // Active (non-archived) applicants — the only ones shown and managed.
  const activeApplicants = applicants.filter((a) => !a.archived);

  // === Member name editing ===
  function canEditMember(m: Member): boolean {
    if (!user) return false;
    if (isParent) return true;
    return m.user_id === user.uid;
  }

  function startEditMember(m: Member) {
    setEditingMemberId(m.user_id);
    setMemberNameDraft(m.display_name || '');
  }

  function cancelEditMember() {
    setEditingMemberId(null);
    setMemberNameDraft('');
  }

  async function saveMemberName(m: Member) {
    if (!db || !membership) return;
    const trimmed = memberNameDraft.trim();
    if (!trimmed) {
      alert('Name cannot be empty.');
      return;
    }
    setSavingMember(true);
    try {
      await updateDoc(
        doc(db, 'households', membership.householdId, 'members', m.user_id),
        { display_name: trimmed, last_active_at: serverTimestamp() }
      );
      cancelEditMember();
    } catch (e) {
      alert('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSavingMember(false);
    }
  }

  async function toggleDeadlineReminders(m: Member, next: boolean) {
    if (!db || !membership) return;
    try {
      const currentPrefs = m.email_preferences ?? { deadline_reminders: true };
      await updateDoc(
        doc(db, 'households', membership.householdId, 'members', m.user_id),
        { email_preferences: { ...currentPrefs, deadline_reminders: next } }
      );
    } catch (e) {
      alert('Update failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  // === Member removal ===
  // Removing a member deletes the member (user account link), removes their
  // uid from the household's member_uids, and clears linked_user_id on any
  // applicant they were linked to. Per the v0.2.24 decision, the applicant
  // and ALL their data are kept — only the person is removed.
  async function confirmRemoveMember() {
    if (!db || !membership || !memberToRemove) return;
    setRemovingMember(true);
    setActionError(null);
    try {
      const hid = membership.householdId;
      const uid = memberToRemove.user_id;

      // 1. Clear linked_user_id on any applicant linked to this member.
      const linkedApplicants = applicants.filter((a) => a.linked_user_id === uid);
      for (const a of linkedApplicants) {
        await updateDoc(doc(db, 'households', hid, 'applicants', a.applicant_id), {
          linked_user_id: null,
          updated_at: serverTimestamp(),
        });
      }

      // 2. Remove the uid from the household member_uids array.
      await updateDoc(doc(db, 'households', hid), {
        member_uids: arrayRemove(uid),
        updated_at: serverTimestamp(),
      });

      // 3. Delete the member doc.
      await deleteDoc(doc(db, 'households', hid, 'members', uid));

      setMemberToRemove(null);
    } catch (e) {
      setActionError(
        'Could not remove member: ' + (e instanceof Error ? e.message : String(e))
      );
    } finally {
      setRemovingMember(false);
    }
  }

  // === Applicant name editing ===
  function startEditApplicant(a: Applicant) {
    setEditingApplicantId(a.applicant_id);
    setApplicantNameDraft(a.display_name || '');
  }

  function cancelEditApplicant() {
    setEditingApplicantId(null);
    setApplicantNameDraft('');
  }

  async function saveApplicantName(a: Applicant) {
    if (!db || !membership) return;
    const trimmed = applicantNameDraft.trim();
    if (!trimmed) {
      alert('Name cannot be empty.');
      return;
    }
    setSavingApplicant(true);
    try {
      await updateDoc(
        doc(db, 'households', membership.householdId, 'applicants', a.applicant_id),
        { display_name: trimmed, updated_at: serverTimestamp() }
      );
      cancelEditApplicant();
    } catch (e) {
      alert('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSavingApplicant(false);
    }
  }

  // === Add a new applicant ===
  // Creates a bare applicant doc. The profile is filled in later via the
  // Profile page, which already tolerates a missing profile doc.
  async function handleAddApplicant() {
    if (!db || !membership) return;
    const trimmed = newApplicantName.trim();
    if (!trimmed) {
      alert('Please enter a name for the new applicant.');
      return;
    }
    setSavingNewApplicant(true);
    setActionError(null);
    try {
      const applicantId = genId('app');
      await setDoc(
        doc(db, 'households', membership.householdId, 'applicants', applicantId),
        {
          applicant_id: applicantId,
          linked_user_id: null,
          display_name: trimmed,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          archived: false,
        }
      );
      setNewApplicantName('');
      setAddingApplicant(false);
    } catch (e) {
      setActionError(
        'Could not add applicant: ' + (e instanceof Error ? e.message : String(e))
      );
    } finally {
      setSavingNewApplicant(false);
    }
  }

  // === Applicant removal (archive) ===
  // Per the v0.2.24 decision (Option B): "removal" archives the applicant
  // rather than hard-deleting. The applicant and all subcollections (
  // scholarships, inbox items, profile, documents) stay in Firestore but are
  // hidden everywhere in the UI — ApplicantContext already filters archived
  // applicants out of the switcher. This is reversible if done by mistake.
  async function confirmRemoveApplicant() {
    if (!db || !membership || !applicantToRemove) return;
    setRemovingApplicant(true);
    setActionError(null);
    try {
      await updateDoc(
        doc(db, 'households', membership.householdId, 'applicants', applicantToRemove.applicant_id),
        { archived: true, updated_at: serverTimestamp() }
      );
      setApplicantToRemove(null);
    } catch (e) {
      setActionError(
        'Could not remove applicant: ' + (e instanceof Error ? e.message : String(e))
      );
    } finally {
      setRemovingApplicant(false);
    }
  }

  // === Link / unlink applicant <-> member ===
  async function handleLinkApplicant(a: Applicant) {
    if (!db || !membership) return;
    const uid = linkDraft[a.applicant_id];
    if (!uid) return;
    setLinkBusy(a.applicant_id);
    setActionError(null);
    try {
      await updateDoc(
        doc(db, 'households', membership.householdId, 'applicants', a.applicant_id),
        { linked_user_id: uid, updated_at: serverTimestamp() }
      );
      setLinkDraft((prev) => {
        const next = { ...prev };
        delete next[a.applicant_id];
        return next;
      });
    } catch (e) {
      setActionError(
        'Could not link applicant: ' + (e instanceof Error ? e.message : String(e))
      );
    } finally {
      setLinkBusy(null);
    }
  }

  async function handleUnlinkApplicant(a: Applicant) {
    if (!db || !membership) return;
    setLinkBusy(a.applicant_id);
    setActionError(null);
    try {
      await updateDoc(
        doc(db, 'households', membership.householdId, 'applicants', a.applicant_id),
        { linked_user_id: null, updated_at: serverTimestamp() }
      );
    } catch (e) {
      setActionError(
        'Could not unlink applicant: ' + (e instanceof Error ? e.message : String(e))
      );
    } finally {
      setLinkBusy(null);
    }
  }

  // Members not already linked to some applicant — candidates for linking.
  function unlinkedMembers(): Member[] {
    const linkedUids = new Set(
      applicants.map((a) => a.linked_user_id).filter((v): v is string => !!v)
    );
    return members.filter((m) => !linkedUids.has(m.user_id));
  }

  // Display name for a member uid (used to show who an applicant is linked to).
  function memberName(uid: string | null): string {
    if (!uid) return '';
    const m = members.find((x) => x.user_id === uid);
    return m?.display_name || m?.email || 'Unknown user';
  }

  // === Invite codes ===
  async function handleGenerateCode() {
    if (!user || !membership) return;
    try {
      const code = await createInviteCode({
        householdId: membership.householdId,
        role: inviteRole,
        applicantId: inviteRole === 'applicant' ? (inviteApplicantId || null) : null,
        createdBy: user.uid,
      });
      const expires = new Date();
      expires.setDate(expires.getDate() + 7);
      setActiveCode({
        code,
        role: inviteRole,
        applicantId: inviteRole === 'applicant' ? (inviteApplicantId || null) : null,
        expiresAt: expires.toISOString(),
      });
      setCopied(false);
    } catch (e) {
      alert('Failed to create code: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function copyInviteUrl() {
    if (!activeCode) return;
    const url = `${window.location.origin}/signup?code=${activeCode.code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      prompt('Copy this invite URL:', url);
    }
  }

  if (loading) return <div className="text-soft">Loading household…</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <PageHeader title="Household" description="Members and invitations." showApplicant={false} />

      {actionError && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
          {actionError}
        </div>
      )}

      {/* ===================== Members ===================== */}
      <Card className="p-4 space-y-3">
        <h2 className="font-medium">Members</h2>
        <ul className="divide-y divide-line">
          {members.map((m) => {
            const isEditing = editingMemberId === m.user_id;
            const editable = canEditMember(m);
            const isSelf = m.user_id === user?.uid;
            return (
              <li key={m.user_id} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={memberNameDraft}
                        onChange={(e) => setMemberNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveMemberName(m);
                          if (e.key === 'Escape') cancelEditMember();
                        }}
                        autoFocus
                        className="max-w-xs"
                        placeholder="Display name"
                      />
                      <Button type="button" size="sm" onClick={() => saveMemberName(m)} disabled={savingMember}>
                        <Check size={12} className="mr-1" />Save
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={cancelEditMember} disabled={savingMember}>
                        <X size={12} />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-ink">{m.display_name || m.email}</p>
                      <p className="text-xs text-soft">{m.email}</p>
                      {(isParent || m.user_id === user?.uid) && (
                        <label className="flex items-center gap-1.5 text-xs text-soft mt-1 cursor-pointer hover:text-soft">
                          <input
                            type="checkbox"
                            checked={m.email_preferences?.deadline_reminders !== false}
                            onChange={(e) => toggleDeadlineReminders(m, e.target.checked)}
                            className="rounded"
                          />
                          <Mail size={11} />
                          <span>Email me deadline reminders</span>
                        </label>
                      )}
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={m.role === 'parent' ? 'indigo' : 'gray'}>{humanize(m.role)}</Badge>
                    {m.status === 'active' && <Badge variant="green">Active</Badge>}
                    {editable && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => startEditMember(m)} title="Edit name">
                        <Pencil size={12} />
                      </Button>
                    )}
                    {/* Parents can remove other members — never themselves. */}
                    {isParent && !isSelf && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setMemberToRemove(m)}
                        title="Remove member"
                      >
                        <Trash2 size={12} className="text-rose-600" />
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {/* ===================== Applicants ===================== */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">Applicants</h2>
          {isParent && !addingApplicant && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setAddingApplicant(true)}>
              <Plus size={14} className="mr-1" />Add applicant
            </Button>
          )}
        </div>

        {/* Add-applicant inline form */}
        {isParent && addingApplicant && (
          <div className="flex items-center gap-2 bg-cream border border-line rounded p-2">
            <Input
              value={newApplicantName}
              onChange={(e) => setNewApplicantName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddApplicant();
                if (e.key === 'Escape') { setAddingApplicant(false); setNewApplicantName(''); }
              }}
              autoFocus
              className="max-w-xs"
              placeholder="New applicant's name"
            />
            <Button type="button" size="sm" onClick={handleAddApplicant} disabled={savingNewApplicant}>
              <Check size={12} className="mr-1" />Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setAddingApplicant(false); setNewApplicantName(''); }}
              disabled={savingNewApplicant}
            >
              <X size={12} />
            </Button>
          </div>
        )}

        <ul className="divide-y divide-line">
          {activeApplicants.map((a) => {
            const isEditing = editingApplicantId === a.applicant_id;
            const candidates = unlinkedMembers();
            return (
              <li key={a.applicant_id} className="py-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={applicantNameDraft}
                        onChange={(e) => setApplicantNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveApplicantName(a);
                          if (e.key === 'Escape') cancelEditApplicant();
                        }}
                        autoFocus
                        className="max-w-xs"
                        placeholder="Display name"
                      />
                      <Button type="button" size="sm" onClick={() => saveApplicantName(a)} disabled={savingApplicant}>
                        <Check size={12} className="mr-1" />Save
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={cancelEditApplicant} disabled={savingApplicant}>
                        <X size={12} />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-ink">{a.display_name}</p>
                      <p className="text-xs text-soft">
                        {a.linked_user_id
                          ? `Linked to ${memberName(a.linked_user_id)}`
                          : 'Not linked to a user yet'}
                      </p>

                      {/* Link controls — parents only, applicant not yet linked */}
                      {isParent && !a.linked_user_id && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <Select
                            value={linkDraft[a.applicant_id] ?? ''}
                            onChange={(e) =>
                              setLinkDraft((prev) => ({ ...prev, [a.applicant_id]: e.target.value }))
                            }
                            className="text-xs max-w-[12rem]"
                          >
                            <option value="">— link to member —</option>
                            {candidates.map((m) => (
                              <option key={m.user_id} value={m.user_id}>
                                {m.display_name || m.email}
                              </option>
                            ))}
                          </Select>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => handleLinkApplicant(a)}
                            disabled={!linkDraft[a.applicant_id] || linkBusy === a.applicant_id}
                          >
                            <Link2 size={12} className="mr-1" />Link
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex items-center gap-2 shrink-0">
                    {!a.linked_user_id && <Badge variant="amber">Awaiting Link</Badge>}
                    {isParent && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => startEditApplicant(a)} title="Edit name">
                        <Pencil size={12} />
                      </Button>
                    )}
                    {/* Unlink — parents only, applicant currently linked */}
                    {isParent && a.linked_user_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUnlinkApplicant(a)}
                        disabled={linkBusy === a.applicant_id}
                        title="Unlink from member"
                      >
                        <Unlink size={12} className="text-amber-600" />
                      </Button>
                    )}
                    {/* Remove (archive) — parents only */}
                    {isParent && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setApplicantToRemove(a)}
                        title="Remove applicant"
                      >
                        <Trash2 size={12} className="text-rose-600" />
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {activeApplicants.length === 0 && (
            <li className="py-3 text-sm text-soft">
              No applicants yet. {isParent && 'Use "Add applicant" above to create one.'}
            </li>
          )}
        </ul>
      </Card>

      {/* ===================== Invite ===================== */}
      {isParent && (
        <Card className="p-4 space-y-3">
          <h2 className="font-medium">Invite a New Member</h2>
          <HelperText>Invitees can use this code at signup to join your household.</HelperText>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'parent' | 'applicant')}>
                <option value="parent">Parent</option>
                <option value="applicant">Applicant</option>
              </Select>
            </div>
            {inviteRole === 'applicant' && (
              <div>
                <Label>Link to Applicant</Label>
                <Select value={inviteApplicantId} onChange={(e) => setInviteApplicantId(e.target.value)}>
                  <option value="">— select applicant —</option>
                  {activeApplicants.filter((a) => !a.linked_user_id).map((a) => (
                    <option key={a.applicant_id} value={a.applicant_id}>{a.display_name}</option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          {inviteRole === 'applicant' && (
            <HelperText>
              Pick which applicant the new member will be. When they join, their
              account links to that applicant automatically.
            </HelperText>
          )}
          <Button onClick={handleGenerateCode} disabled={inviteRole === 'applicant' && !inviteApplicantId}>
            <UserPlus size={16} className="mr-1" /> Generate invite code
          </Button>

          {activeCode && (
            <div className="mt-4 p-3 bg-forest-50 border border-forest-200 rounded">
              <p className="text-xs text-soft">Invite code:</p>
              <p className="text-2xl font-mono font-semibold text-forest-800 my-1">{activeCode.code}</p>
              <p className="text-xs text-soft">
                Role: <strong>{humanize(activeCode.role)}</strong> · expires in 7 days
              </p>
              <Button size="sm" variant="secondary" onClick={copyInviteUrl} className="mt-2">
                {copied ? <Check size={12} className="mr-1" /> : <Copy size={12} className="mr-1" />}
                {copied ? 'Copied!' : 'Copy signup URL'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ===================== Subscription (v0.2.43) ===================== */}
      {billing.billingEnabled && isFounder && (
        <Card className="p-4 space-y-3">
          <h2 className="font-medium">Subscription</h2>
          {billing.hasActiveSub && billingStatus !== 'lapsed' ? (
            <>
              <p className="text-sm text-soft">
                Your household subscription is{' '}
                <span className="font-medium text-emerald-700">active</span> — $14.99/year, covers up
                to 8 family members.
              </p>
              <Button variant="secondary" onClick={handleManageSubscription} disabled={billingBusy}>
                <CreditCard size={14} className="mr-1" />
                {billingBusy ? 'Opening…' : 'Manage subscription'}
              </Button>
              <HelperText>Update your card, view invoices, or cancel anytime via Stripe.</HelperText>
            </>
          ) : (
            <>
              <p className="text-sm text-soft">
                No active subscription. Subscribe to keep your household active — $14.99/year, up to
                8 family members.
              </p>
              <Button onClick={handleSubscribeNow} disabled={billingBusy}>
                <CreditCard size={14} className="mr-1" />
                {billingBusy ? 'Opening…' : 'Subscribe'}
              </Button>
            </>
          )}
          {billingErr && <p className="text-sm text-rose-600">{billingErr}</p>}
        </Card>
      )}

      {/* ===================== Our companion app (cross-sell, always shown) ===================== */}
      <CompanionCard />

      <p className="mt-6 text-center text-xs text-soft">
        Need help with billing or your account? Email{' '}
        <a href="mailto:homescholarpro@gmail.com" className="text-forest-700 hover:underline">homescholarpro@gmail.com</a>
      </p>

      {/* ===================== Member removal dialog ===================== */}
      {memberToRemove && (
        <ConfirmDialog
          title="Remove this member?"
          confirmLabel={removingMember ? 'Removing…' : 'Remove member'}
          confirmDisabled={removingMember}
          onCancel={() => !removingMember && setMemberToRemove(null)}
          onConfirm={confirmRemoveMember}
          danger
        >
          <p>
            <strong>{memberToRemove.display_name || memberToRemove.email}</strong> will be
            removed from this household and will lose access.
          </p>
          <p className="mt-2 text-soft">
            Any applicant they were linked to — and all of that applicant's
            scholarships, inbox items, and profile — will be kept. Only this
            person's account is removed.
          </p>
        </ConfirmDialog>
      )}

      {/* ===================== Applicant removal dialog ===================== */}
      {applicantToRemove && (
        <ConfirmDialog
          title="Remove this applicant?"
          confirmLabel={removingApplicant ? 'Removing…' : 'Remove applicant'}
          confirmDisabled={removingApplicant}
          onCancel={() => !removingApplicant && setApplicantToRemove(null)}
          onConfirm={confirmRemoveApplicant}
          danger
        >
          <p>
            <strong>{applicantToRemove.display_name}</strong> will be removed from
            the tracker.
          </p>
          <p className="mt-2 text-soft">
            This hides the applicant and everything attached to them —
            scholarships, inbox items, profile, documents, and target schools —
            from the entire app. Their data is retained behind the scenes and an
            administrator can restore them, but they will no longer appear
            anywhere in the tracker.
          </p>
          {applicantToRemove.linked_user_id && (
            <p className="mt-2 text-amber-700">
              Note: a member account is linked to this applicant. That account
              stays in the household but will no longer have an applicant.
            </p>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}

// =============================================================================
// ConfirmDialog — a simple modal confirmation used for destructive actions.
// =============================================================================

function ConfirmDialog({
  title,
  children,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  onCancel,
  danger,
}: {
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-forest-800/40 flex items-center justify-center p-4 z-50"
      onClick={onCancel}
    >
      <div
        className="bg-paper rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold text-ink mb-2">{title}</h3>
        <div className="text-sm text-soft">{children}</div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={confirmDisabled}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
